/* eslint-disable max-lines */
import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Editor, MaybeTooltip } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { TriangleDownIcon, UnknownUserIcon } from '@modernloop/shared/icons';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Alert as MuiAlert,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import {
  CreateTaskStep_ApplicationFragment,
  CreateTaskStep_JobStageFragment,
  TaskCreationSource,
} from 'src/generated/mloop-graphql';

import { FilterSelectGroup } from 'src/components/FilterSelect';
import { FCWithFragments } from 'src/components/types';

import {
  selectButtonLabelSxProps_DO_NOT_USE,
  selectButtonProps_DO_NOT_USE,
} from 'src/constants/selectButtonProps_DO_NOT_USE';

import TaskTags from 'src/entities/Filters/TaskTags';
import InterviewPlan from 'src/entities/InterviewPlan';
import getSaveInterviewPlanInput from 'src/entities/InterviewPlan/getSaveInterviewPlanInput';
import useInterviewPlanEntityHasInterviewHiddenFromCandidate from 'src/entities/InterviewPlan/useInterviewPlanHasInterviewHiddenFromCandidate';
import { useResetInterviewPlanToBaseJobStage } from 'src/entities/InterviewPlan/useResetInterviewPlanToBaseJobStage';
import MultiMemberSelect from 'src/entities/Select/MemberSelect/MultiMemberSelect';
import QueueSelect from 'src/entities/Select/QueueSelect/QueueSelect';
import AvatarFacePile from 'src/entities/common/AvatarFacePile';

import useEmployeeId from 'src/hooks/useEmployeeId';

import CreateTaskJobStageSelect from 'src/views-new/ScheduleTask/CreateModals/shared/CreateTaskJobStageSelect';
import AssigneeSelect from 'src/views-new/ScheduleTask/ScheduleTaskDetails/Common/ScheduleTaskAssignee/AssigneeSelect';

import AvailabilityInput from './AvailabilityInput';
import CreateDetailsRow from './CreateDetailsRow';
import { TaskCreateInputWrapper } from './types';

interface Props {
  isAvailabilityEnabled?: boolean;
  jobStageLoading: boolean;
  input: TaskCreateInputWrapper;
  source: TaskCreationSource;
  onInputChange: (input: TaskCreateInputWrapper) => void;
  setIsImportingAvailability: (isImporting: boolean) => void;
  onInterviewPlanChange: (interviewPlan: CreateTaskStep_JobStageFragment) => void;
}

type Fragments = {
  application: CreateTaskStep_ApplicationFragment;
  jobStage: CreateTaskStep_JobStageFragment | undefined;
};

const CreateTaskStep: FCWithFragments<Fragments, Props> = ({
  application,
  jobStage,
  jobStageLoading,
  onInputChange,
  input,
  source,
  isAvailabilityEnabled,
  setIsImportingAvailability,
  onInterviewPlanChange,
}) => {
  const { t } = useTranslation();
  const currentEmployeeId = useEmployeeId();
  const job = application?.job;
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const { resetPlan } = useResetInterviewPlanToBaseJobStage({
    jobStageId: input.jobStageId,
    customJobStageId: input.customJobStageId,
    doNotPersist: true,
  });
  const hasInterviewHiddenFromCandidate = useInterviewPlanEntityHasInterviewHiddenFromCandidate({
    interviewPlan: jobStage,
  });

  if (!job || !application.jobStage) {
    return null;
  }

  return (
    <Stack sx={{ width: '100%' }} spacing={2}>
      <Typography fontWeight={600}>{t('hiring-dashboard-tasks:tasks.create.schedulingDetails')}</Typography>
      <CreateDetailsRow
        label={t('hiring-dashboard-tasks:tasks.create.assignTo')}
        component={
          <Box sx={{ width: '50%' }}>
            <AssigneeSelect
              onChange={(newMember) => {
                if (!newMember || newMember?.employee?.id === null) {
                  onInputChange({ ...input, assigneeEmployeeId: null, assignee: null });
                  return;
                }
                onInputChange({
                  ...input,
                  assigneeEmployeeId: newMember?.employee?.id,
                  assignee: newMember.employee,
                });
              }}
              getLabel={() => {
                let label;
                let startIcon;

                if (!input.assignee) {
                  label = t('hiring-dashboard-tasks:tasks.create.unassigned');
                  startIcon = <UnknownUserIcon />;
                } else {
                  label = (
                    <Stack direction="row" spacing={1} sx={selectButtonLabelSxProps_DO_NOT_USE}>
                      <Box
                        sx={{
                          marginLeft: '-4px',
                        }}
                      >
                        <Avatar
                          alt={input.assignee.fullName ?? ''}
                          src={input.assignee.internalProfilePictureUrl ?? undefined}
                        />
                      </Box>

                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        <MaybeTooltip title={input.assignee.fullName ?? ''} label={input.assignee.fullName ?? ''} />
                      </Box>
                    </Stack>
                  );
                }

                return (
                  <Button
                    {...selectButtonProps_DO_NOT_USE}
                    data-testid="schedule-task-assignee-select-button"
                    startIcon={startIcon}
                    sx={selectButtonProps_DO_NOT_USE.sx}
                  >
                    {label}
                  </Button>
                );
              }}
            />
          </Box>
        }
      />
      <CreateDetailsRow
        label={t('hiring-dashboard-tasks:tasks.create.additionalSubscribers')}
        component={
          <Box width="50%">
            <MultiMemberSelect
              header={
                <Box mt={(input.subscriberEmployeeIds?.length ?? 0) > 0 ? 2.5 : 0}>
                  <Alert severity="info">{t('hiring-dashboard-tasks:tasks.create.subscriberInfo')}</Alert>
                </Box>
              }
              sortMembers={(a, b) => {
                // Sort members so all subscribers are at the top
                // Then either the coordinator or recruiter
                // Then alphabetically with the current user at the top

                if (input.subscriberEmployeeIds?.includes(a.employee?.id)) {
                  return -1;
                }
                if (input.subscriberEmployeeIds?.includes(b.employee?.id)) {
                  return 1;
                }

                if (a.employee?.id === application.coordinator?.id) {
                  return -1;
                }
                if (b.employee?.id === application.coordinator?.id) {
                  return 1;
                }

                if (a.employee?.id === application.recruiter?.id) {
                  return -1;
                }
                if (b.employee?.id === application.recruiter?.id) {
                  return 1;
                }

                if (a.employee?.id === currentEmployeeId) {
                  return -1;
                }
                if (b.employee?.id === currentEmployeeId) {
                  return 1;
                }

                return a.employee?.fullName?.localeCompare(b.employee?.fullName ?? '') ?? 0;
              }}
              groupBy={(option) => {
                if (input.subscriberEmployeeIds?.includes(option.employee?.id)) {
                  return FilterSelectGroup.SELECTED;
                }

                if (option.employee?.id === application.coordinator?.id) {
                  return FilterSelectGroup.SUGGESTED;
                }

                if (option.employee?.id === application.recruiter?.id) {
                  return FilterSelectGroup.SUGGESTED;
                }

                return FilterSelectGroup.OTHER;
              }}
              placeholderText={t('hiring-dashboard-tasks:tasks.search')}
              selectedEmployeeIds={input.subscriberEmployeeIds ?? []}
              getLabel={(selectedMembers) => {
                const label =
                  selectedMembers.length > 0 ? (
                    <Stack direction="row" spacing={1} maxWidth="100%" paddingRight={1.5}>
                      <Box
                        sx={{
                          marginLeft: '-4px',
                        }}
                      >
                        <Box ml={1}>
                          <AvatarFacePile
                            size="sm"
                            max={1}
                            interviewers={selectedMembers?.map((val) => {
                              return {
                                interviewerId: val.employee?.id,
                                interviewer: {
                                  id: val.employee?.id,
                                  fullName: val.employee?.fullName,
                                  internalProfilePictureUrl: val.employee?.internalProfilePictureUrl,
                                },
                                responseStatus: null,
                                isOptional: false,
                              };
                            })}
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        <MaybeTooltip
                          label={selectedMembers?.map((val) => val.employee?.fullName).join(', ')}
                          title={selectedMembers?.map((val) => val.employee?.fullName).join(', ')}
                        />
                      </Box>
                    </Stack>
                  ) : (
                    <Typography color="text.disabled">
                      {t('hiring-dashboard-tasks:tasks.create.searchPeople')}
                    </Typography>
                  );
                return (
                  <Button
                    size="medium"
                    fullWidth
                    endIcon={<TriangleDownIcon />}
                    sx={{
                      justifyContent: 'flex-start',
                      borderRadius: '6px',
                      '& .MuiButton-endIcon': {
                        position: 'absolute',
                        right: 12,
                      },
                    }}
                  >
                    {label}
                  </Button>
                );
              }}
              onChange={(newMembers) => {
                const employeeIds = newMembers.map((member) => member.employee?.id);
                onInputChange({ ...input, subscriberEmployeeIds: employeeIds });
              }}
            />
          </Box>
        }
      />
      <CreateDetailsRow
        label={t('hiring-dashboard-tasks:tasks.create.addToQueue')}
        component={
          <Box width="50%">
            <QueueSelect
              groupQueue
              defaultTaskQueueId={application.job?.jobSettings.defaultTaskQueue?.id}
              onOptionChange={(queueId) => {
                onInputChange({ ...input, taskQueueId: queueId });
              }}
              selectedTaskQueueId={input.taskQueueId}
            />
          </Box>
        }
      />
      <CreateDetailsRow
        label={t('hiring-dashboard-tasks:tasks.column.stage')}
        component={
          <Box width="50%">
            {application.atsApplication?.canMoveJobStage && (
              <CreateTaskJobStageSelect
                applicationJobStageId={application.jobStage.id}
                job={job}
                selectedJobStageId={input.jobStageId}
                onChange={(id) => {
                  if (id !== input.jobStageId) {
                    onInputChange({ ...input, jobStageId: id, customJobStageId: null });
                  }
                }}
              />
            )}
            {!application.atsApplication?.canMoveJobStage && <Typography>{application.jobStage.name}</Typography>}
          </Box>
        }
      />
      <Box p={0}>
        <FormControlLabel
          data-testid="urgent-switch-label"
          labelPlacement="start"
          label={t('hiring-dashboard-tasks:tasks.create.markTaskAsUrgent')}
          sx={{ width: '100%', justifyContent: 'space-between' }}
          control={
            <Switch
              data-testid="urgent-switch"
              checked={input.isUrgent}
              onChange={(_, urgent) => {
                onInputChange({ ...input, isUrgent: urgent });
              }}
            />
          }
        />
      </Box>
      <TaskTags
        onChange={(newTags) => {
          if (newTags && newTags.length === 0) {
            onInputChange({ ...input, taskTags: null, tagIds: null });
            return;
          }
          onInputChange({ ...input, taskTags: newTags, tagIds: newTags.map((tag) => tag.id) });
        }}
        selectedTaskTags={input.taskTags ?? []}
      />
      <Divider />
      <Typography variant="subtitle1">{t('hiring-dashboard-tasks:tasks.create.interviewsToBeScheduled')}</Typography>
      {source === TaskCreationSource.SelfScheduleRequest && hasInterviewHiddenFromCandidate && (
        <MuiAlert severity="error">
          <AlertTitle>{t('hiring-dashboard-tasks:tasks.create.selfScheduleUnavailable')}</AlertTitle>
          {t('hiring-dashboard-tasks:tasks.create.selfScheduleUnavailableDescription')}
        </MuiAlert>
      )}
      <Box>
        <InterviewPlan
          onResetToJobStageDefault={async () => {
            const newInterviewPlan = await resetPlan();

            if (newInterviewPlan) {
              onInterviewPlanChange({ ...newInterviewPlan });

              const interviewPlanInput = getSaveInterviewPlanInput(
                { interviewPlan: newInterviewPlan },
                { finalJobStageId: input.customJobStageId, collaborationLinkEnabled }
              );
              onInputChange({
                ...input,
                customInterviewPlan: !orgBreakImprovementsEnabled ? interviewPlanInput.groups : undefined,
                interviewPlan: orgBreakImprovementsEnabled
                  ? {
                      groups: interviewPlanInput.groups,
                      schedulingWindow: interviewPlanInput.schedulingWindow
                        ? { seconds: interviewPlanInput.schedulingWindow.seconds }
                        : undefined,
                      excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
                    }
                  : undefined,
              });
            }
          }}
          schedulable={false}
          job={job || undefined}
          jobStage={jobStage}
          jobStageLoading={jobStageLoading}
          onUpdated={(newInterviewPlan) => {
            onInterviewPlanChange(newInterviewPlan);

            const interviewPlanInput = getSaveInterviewPlanInput(
              { interviewPlan: newInterviewPlan },
              { finalJobStageId: input.customJobStageId, collaborationLinkEnabled }
            );
            onInputChange({
              ...input,
              customInterviewPlan: !orgBreakImprovementsEnabled ? interviewPlanInput.groups : undefined,
              interviewPlan: orgBreakImprovementsEnabled
                ? {
                    groups: interviewPlanInput.groups,
                    schedulingWindow: interviewPlanInput.schedulingWindow
                      ? { seconds: interviewPlanInput.schedulingWindow.seconds }
                      : undefined,
                    excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
                  }
                : undefined,
            });
          }}
        />
      </Box>
      <Box p={0}>
        <FormControlLabel
          labelPlacement="start"
          label={t('hiring-dashboard-tasks:tasks.create.scheduleDebriefAfterInterview')}
          sx={{ width: '100%', justifyContent: 'space-between' }}
          control={
            <Switch
              data-testid="debrief-switch"
              checked={input.isDebriefRequired}
              onChange={(_, shouldDebrief) => {
                onInputChange({ ...input, isDebriefRequired: shouldDebrief });
              }}
            />
          }
        />
      </Box>
      {isAvailabilityEnabled && <Divider />}
      {isAvailabilityEnabled && (
        <AvailabilityInput
          applicationId={application.id}
          candidateTimezone={input.candidateTimezone}
          value={input.availability ?? []}
          onChange={(timezone, newTimes) => {
            onInputChange({ ...input, availability: newTimes, candidateTimezone: timezone });
          }}
          setIsImportingAvailability={setIsImportingAvailability}
        />
      )}
      <Divider />
      <Typography variant="subtitle1">{t('hiring-dashboard-tasks:tasks.create.commentForRecruitingTeam')}</Typography>
      <Editor
        defaultValue={input.note || ''}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: '6px',
          '& .ql-editor': { minHeight: '104px', maxHeight: '104px' },
        }}
        formats={[]}
        modules={{
          toolbar: false,
          clipboard: {
            matchVisual: false,
          },
        }}
        onChange={(value, _, __, editor) => {
          const cleanedText = editor.getText().trim();
          if (cleanedText) {
            onInputChange({ ...input, note: cleanedText });
          } else {
            onInputChange({ ...input, note: '' });
          }
        }}
      />
    </Stack>
  );
};

CreateTaskStep.fragments = {
  application: gql`
    ${InterviewPlan.fragments.job}
    ${CreateTaskJobStageSelect.fragments.job}
    fragment CreateTaskStep_application on Application {
      id
      jobStage {
        id
        name
      }
      job {
        id
        ...InterviewPlan_job
        ...CreateTaskJobStageSelect_job
        jobSettings {
          id
          defaultTaskQueue {
            id
          }
        }
      }
      candidate {
        id
      }
      coordinator {
        id
        fullName
      }
      recruiter {
        id
        fullName
      }
      atsApplication {
        atsId
        canMoveJobStage
      }
    }
  `,
  jobStage: gql`
    ${InterviewPlan.fragments.jobStage}
    ${useInterviewPlanEntityHasInterviewHiddenFromCandidate.fragments.interviewPlan}
    fragment CreateTaskStep_jobStage on JobStage {
      id
      ...InterviewPlan_jobStage
      ...useInterviewPlanHasInterviewHiddenFromCandidate_interviewPlan
    }
  `,
};

export default CreateTaskStep;
